import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Import all locale files
import en from '../locales/en.json';
import de from '../locales/de.json';
import es from '../locales/es.json';
import fr from '../locales/fr.json';
import jp from '../locales/jp.json';

// Available languages configuration
export const LANGUAGES = {
  en: { name: 'English', flag: '🇺🇸', locale: en },
  de: { name: 'Deutsch', flag: '🇩🇪', locale: de },
  es: { name: 'Español', flag: '🇪🇸', locale: es },
  fr: { name: 'Français', flag: '🇫🇷', locale: fr },
  jp: { name: '日本語', flag: '🇯🇵', locale: jp },
};

// Create context
const I18nContext = createContext(null);

// Storage key for language preference
const STORAGE_KEY = 'degen_battles_language';

// Get nested value from object using dot notation
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

// Provider component
export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Try to get saved language preference
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LANGUAGES[saved]) {
      return saved;
    }
    
    // Try to detect browser language
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && LANGUAGES[browserLang]) {
      return browserLang;
    }
    
    // Default to English
    return 'en';
  });
  
  const [translations, setTranslations] = useState(LANGUAGES[language].locale);
  
  // Update translations when language changes
  useEffect(() => {
    setTranslations(LANGUAGES[language].locale);
    localStorage.setItem(STORAGE_KEY, language);
    
    // Update document lang attribute
    document.documentElement.lang = language;
  }, [language]);
  
  // Translation function
  const t = useCallback((key, params = {}) => {
    let text = getNestedValue(translations, key);
    
    // Fallback to English if translation not found
    if (text === null) {
      text = getNestedValue(LANGUAGES.en.locale, key);
    }
    
    // If still not found, return the key
    if (text === null) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }
    
    // Replace params in text (e.g., "Hello {name}" -> "Hello John")
    if (typeof text === 'string' && Object.keys(params).length > 0) {
      Object.entries(params).forEach(([param, value]) => {
        text = text.replace(new RegExp(`{${param}}`, 'g'), value);
      });
    }
    
    return text;
  }, [translations]);
  
  // Set language function
  const setLanguage = useCallback((lang) => {
    if (LANGUAGES[lang]) {
      setLanguageState(lang);
    } else {
      console.warn(`Language ${lang} not supported`);
    }
  }, []);
  
  // Get current language info
  const currentLanguage = LANGUAGES[language];
  
  const value = {
    language,
    setLanguage,
    t,
    currentLanguage,
    availableLanguages: LANGUAGES,
  };
  
  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

// Custom hook to use i18n
export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

export default I18nProvider;
